'use client';

import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Divider,
  Link,
} from '@mui/material';
import {
  CloudUpload,
  InsertDriveFile,
  Delete,
  Description,
  Image,
  PictureAsPdf,
  OpenInNew,
} from '@mui/icons-material';

interface DropzoneProps {
  fileUrls?: string[];
  isUploadingFiles?: boolean;
  onFilesDropped?: (files: File[]) => void;
  onFileRemove?: (fileIndex: number) => void;
  accept?: Record<string, string[]>;
  maxFiles?: number;
  maxSize?: number;
}

export function Dropzone({
  fileUrls = [],
  isUploadingFiles,
  onFilesDropped,
  onFileRemove,
  accept = {
    'application/pdf': ['.pdf'],
    'image/*': ['.jpg', '.jpeg', '.png'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
      '.docx',
    ],
  },
  maxFiles = 5,
  maxSize = 20 * 1024 * 1024, // 20MB
}: DropzoneProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      onFilesDropped?.(acceptedFiles);
    },
    [onFilesDropped],
  );

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    fileRejections,
  } = useDropzone({
    disabled: isUploadingFiles,
    onDrop,
    accept,
    maxFiles,
    maxSize,
  });

  const getFileIcon = (fileUrl: string) => {
    const extension = fileUrl.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') return <PictureAsPdf />;
    if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension || ''))
      return <Image />;
    return <Description />;
  };

  const getFileName = (fileUrl: string) => {
    return fileUrl.split('/').pop() || fileUrl;
  };

  const getFileType = (fileUrl: string) => {
    const extension = fileUrl.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf':
        return 'PDF dokument';
      case 'jpg':
      case 'jpeg':
        return 'JPEG obrázek';
      case 'png':
        return 'PNG obrázek';
      case 'doc':
        return 'Word dokument';
      case 'docx':
        return 'Word dokument';
      default:
        return 'Soubor';
    }
  };

  return (
    <Box>
      <Paper
        {...getRootProps()}
        elevation={0}
        sx={{
          border: '2px dashed',
          borderColor: isDragActive
            ? 'primary.main'
            : isDragReject
              ? 'error.main'
              : 'grey.300',
          backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
          borderRadius: 2,
          p: 3,
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            borderColor: 'primary.main',
            backgroundColor: 'action.hover',
          },
          minHeight: 120,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
        }}
      >
        <input {...getInputProps()} />

        <IconButton
          sx={{
            color: isDragActive
              ? 'primary.main'
              : isDragReject
                ? 'error.main'
                : 'grey.500',
            fontSize: '2.5rem',
            mb: 1,
          }}
        >
          {isDragActive ? <CloudUpload /> : <InsertDriveFile />}
        </IconButton>

        {isUploadingFiles ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Nahravám dokumenty...
            </Typography>
          </Box>
        ) : (
          <>
            <Typography
              variant="h6"
              color={
                isDragActive
                  ? 'primary.main'
                  : isDragReject
                    ? 'error.main'
                    : 'text.primary'
              }
              sx={{ fontWeight: 500 }}
            >
              {isDragActive
                ? 'Přetáhněte dokumenty sem'
                : isDragReject
                  ? 'Nepodporovaný typ souboru'
                  : 'Přetáhněte dokumenty sem nebo klikněte pro výběr'}
            </Typography>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Povolené formáty: PDF, JPG, PNG, DOC, DOCX (max. {maxFiles}{' '}
              dokumentů, {Math.round(maxSize / 1024 / 1024)}MB)
            </Typography>
          </>
        )}

        {fileRejections.length > 0 && (
          <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
            Některé dokumenty byly odmítnuty. Zkontrolujte formát a velikost.
          </Typography>
        )}
      </Paper>

      {fileUrls.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Nahrané dokumenty ({fileUrls.length})
          </Typography>
          <Paper elevation={1} sx={{ borderRadius: 1 }}>
            <List dense>
              {fileUrls.map((fileUrl, index) => (
                <React.Fragment key={`${fileUrl}-${index}`}>
                  <ListItem
                    secondaryAction={
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton
                          edge="end"
                          aria-label="otevřít"
                          component={Link}
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="small"
                        >
                          <OpenInNew />
                        </IconButton>
                        {onFileRemove && (
                          <IconButton
                            edge="end"
                            aria-label="odstranit"
                            onClick={() => onFileRemove(index)}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        )}
                      </Box>
                    }
                  >
                    <ListItemIcon>{getFileIcon(fileUrl)}</ListItemIcon>
                    <ListItemText
                      primary={getFileName(fileUrl)}
                      secondary={
                        <Typography
                          component={'div'}
                          sx={{
                            display: 'flex',
                            gap: 1,
                            alignItems: 'center',
                            mt: 0.5,
                          }}
                        >
                          <Chip
                            component={'div'}
                            label={getFileType(fileUrl)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < fileUrls.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
